<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div>
    <Header />

    <section class="hero">
      <div
        v-for="(image, index) in banners"
        :key="image"
        class="hero-slide"
        :class="{ active: index === current }"
        :style="{ backgroundImage: `url(${image})` }"
      ></div>
      <div class="dots">
        <button
          v-for="(_, index) in banners"
          :key="index"
          :class="{ active: index === current }"
          :aria-label="`第${index + 1}张轮播图`"
          @click="current = index"
        ></button>
      </div>
    </section>

    <section class="intro">
      <img
        ref="introImg"
        src="/images/dierye-2.jpg"
        alt="鑫谷程序员课程与教学"
      />
    </section>

    <section id="courses" class="courses-section">
      <div class="course-intro">
        <!-- <small>01 / COURSES</small> -->
        <h2>面向产业互联网的<br /><strong>职业课程</strong></h2>
      </div>
      <div class="course-grid">
        <article
          v-for="(course, index) in courses"
          :key="course.title"
          class="course-card"
          :class="`course-card-${index + 1}`"
        >
          <div class="course-shape" :class="`shape-${index + 1}`"></div>
          <b>{{ course.title }}</b>
          <p>{{ course.desc }}</p>
          <router-link
            class="course-arrow"
            :to="course.to"
            :aria-label="`了解${course.title}`"
            >-&gt;</router-link
          >
        </article>
      </div>
    </section>

    <section id="teachers" class="teacher-section">
      <div class="teacher-heading">
        <!-- <small>02 / MENTORS</small> -->
        <h2>老师介绍</h2>
        <div class="teacher-tabs">
          <button
            :class="{ active: teacherType === 'lecturer' }"
            @click="changeTeacherType('lecturer')"
          >
            讲课老师
          </button>
          <button
            :class="{ active: teacherType === 'career' }"
            @click="changeTeacherType('career')"
          >
            就业老师
          </button>
        </div>
      </div>
      <div class="teacher-stage">
        <el-carousel
          ref="teacherCarousel"
          class="teacher-carousel"
          :interval="4500"
          type="card"
          arrow="always"
          indicator-position="outside"
          @change="handleTeacherCarouselChange"
          @pointerdown="handleTeacherPointerDown"
          @pointerup="handleTeacherPointerUp"
          @pointercancel="handleTeacherPointerCancel"
        >
          <el-carousel-item
            v-for="teacher in currentTeacherList()"
            :key="teacher.title"
          >
            <article class="teacher-card">
              <img :src="teacher.avatar" :alt="teacher.title" />
              <div class="teacher-card-content">
                <h3>{{ teacher.title }}</h3>
                <small>{{ teacher.tag }}</small>
                <p>{{ teacher.desc }}</p>
              </div>
            </article>
          </el-carousel-item>
        </el-carousel>
      </div>
      <router-link class="teacher-more-link" to="/teachers">
        更多老师<span aria-hidden="true">&gt;</span>
      </router-link>
    </section>

    <section id="news" class="news-section">
      <div class="news-title"><h2>新闻资讯</h2></div>
      <div class="news-content">
        <div class="news-list">
          <router-link
            v-for="item in news"
            :key="item.slug"
            class="news-card-link"
            :to="{ name: 'news-detail', params: { slug: item.slug } }"
          >
            <article class="news-item">
              <div
                class="news-item-image"
                role="img"
                :aria-label="item.title"
                :style="{ backgroundImage: `url(${item.image})` }"
              ></div>
              <div>
                <small>{{ item.tag }}</small>
                <h3>{{ item.title }}</h3>
                <span class="news-detail-link">查看详情 -&gt;</span>
              </div>
            </article>
          </router-link>
        </div>
        <router-link
          class="news-card-link news-feature-link"
          :to="{
            name: 'news-detail',
            params: { slug: featuredNews.slug },
          }"
        >
          <article class="news-feature">
            <div
              class="news-feature-image"
              :style="{ backgroundImage: `url(${featuredNews.image})` }"
            ></div>
            <small>{{ featuredNews.tag }}</small>
            <h3>{{ featuredNews.title }}</h3>
            <time>{{ featuredNews.date }}</time>
          </article>
        </router-link>
      </div>
    </section>

    <section id="about" class="about">
      <small>ABOUT XINGU</small>
      <h2>让技术教育真正<br /><em>服务职业成长</em></h2>
    </section>

    <Footer />
    <button class="back-top" aria-label="返回顶部" @click="backTop">
      ↑<span>返回顶部</span>
    </button>
  </div>
</template>

<script setup>
import { onMounted, onUnmounted, ref } from "vue";
import { featuredNews, news } from "../data/news";
import Header from "../components/layout/Header.vue";
import Footer from "../components/layout/Footer.vue";

const banners = [
  "/images/轮播1-1.jpg",
  "/images/轮播2-2.jpg",
  "/images/轮播3-3.jpg",
];
const courses = [
  {
    title: "Java全栈开发",
    desc: "系统掌握Java语法、Spring Boot、数据库与微服务，完成企业级项目开发。",
    to: { name: "learning-path", hash: "#learning-path-top" },
  },
  {
    title: "智能应用开发",
    desc: "聚焦智能业务系统、工作流编排与AI接口集成，训练可落地的应用开发能力。",
    to: { name: "learning-path", hash: "#intelligent-app" },
  },
  {
    title: "大模型应用开发",
    desc: "学习提示词工程、RAG检索增强、Agent与模型微调，构建专业级AI应用。",
    to: { name: "learning-path", hash: "#large-model" },
  },
  {
    title: "AI运维工程师",
    desc: "掌握Linux、Docker、Kubernetes与监控告警，完成云原生平台运维实战。",
    to: { name: "learning-path", hash: "#ai-ops" },
  },
  {
    title: "AIGC视觉创意设计",
    desc: "运用AI完成品牌视觉、海报与商业内容设计，提升创意表达与交付效率。",
    to: { name: "learning-path", hash: "#aigc-design" },
  },
];
const teachers = [
  {
    title: "Mr. Han老师",
    tag: "前 Oracle 首席架构师",
    desc: "前Oracle北京研发中心核心架构师，8年云计算架构实战经验，服务50+银行、运营商标杆企业，培养1000+IT技术人才。独创“JAVA开发筑基+大模型应用落地”双主线课程，助你快速掌握AI时代核心开发能力，实现薪资跃迁。",
    avatar: "/images/teachers/Mr.Han.jpg",
  },
  {
    title: "常胜老师",
    tag: "大厂分布式架构专家",
    desc: "10年一线大厂分布式架构实战专家，曾任职于中科软、浪潮、字节跳动行业头部企业。拒绝照本宣科，专注深度辅导与学情追踪，打通理论到企业开发的最后一公里，助力学员高薪就业。",
    avatar: "/images/teachers/常胜老师.jpg",
  },
  {
    title: "宇畔老师",
    tag: "全栈开发架构专家",
    desc: "企业级大数据与后端架构实战专家，商智云管理系统核心架构师。精通JAVA全栈开发与微服务架构，将企业真实场景融入课堂，全方位护航学员技术成长，助力学员快速掌握企业级开发能力。",
    avatar: "/images/teachers/宇畔老师.jpg",
  },
  {
    title: "景程老师",
    tag: "前阿里资深AI应用架构师",
    desc: "前阿里资深AI应用架构师，10年AI+JAVA全栈研发经验，主导千万级AI业务落地。将大厂标准化研发流程融入课堂，带你从0到1掌握AI应用全流程开发，对接高薪岗位。",
    avatar: "/images/teachers/景程老师.jpg",
  },
  {
    title: "南枫老师",
    tag: "10 年品牌设计总监  5 年设计教学实战导师",
    desc: "10年品牌设计总监，5年设计教学经验，服务数十家大型企业，精通全案设计与主流设计软件。因材施教，以真实项目实战教学，培养上千名学员，带你成为独当一面的高素质设计人才。",
    avatar: "/images/teachers/南枫老师.jpg",
  },
];
const careerTeachers = [
  {
    title: "远航老师",
    tag: "10年IT行业资深就业导师  前互联网大厂HRBP",
    desc: "10年IT行业资深就业导师，前国内顶尖互联网大厂HRBP，累计辅导3000+学员，高薪offer率85%，提供全流程一对一就业辅导。",
    avatar: "/images/teachers/远航老师.jpg",
  },
  {
    title: "乐途老师",
    tag: "96%转行学院就业率  零基础转行就业引路人",
    desc: "10年IT职业教育就业专家，累计辅导2500+零基础转行学员，就业率96%。擅长定制专属事业规划与学习路径，全流程一对一就业护航，从0到1带你完成IT高薪职业转型。",
    avatar: "/images/teachers/乐途老师.jpg",
  },
  {
    title: "星文老师",
    tag: "8年AI行业就业辅导专家  AI赛道高薪岗位匹配师",
    desc: "8年AI行业就业辅导专家，累计辅导1800+AI方向学员，就业率92%，擅长AI赛道岗位精准匹配，定制专属就业提升方案，带你进入AI高薪赛道。",
    avatar: "/images/teachers/星文老师.jpg",
  },
  {
    title: "正阳老师",
    tag: "前互联网大厂技术面试官 12年JAVA开发招聘与面试专家",
    desc: "12年JAVA开发招聘与面试专家，前美团技术面试官，累计面试2000+候选人。辅导2200+JAVA学员，超60%学员入职后薪资翻倍。帮你搭建面试体系，攻克面试难点，拿到高薪offer。",
    avatar: "/images/teachers/正阳老师.jpg",
  },
];
const introImg = ref(null);
const teacherCarousel = ref(null);
const current = ref(0);
const teacherType = ref("lecturer");
const teacherIndex = ref(0);
const activeTeacher = ref(teachers[0]);
let bannerTimer;
let teacherTimer;
let teacherPointerStartX = null;

function currentTeacherList() {
  return teacherType.value === "lecturer" ? teachers : careerTeachers;
}
function updateTeacher() {
  const list = currentTeacherList();
  activeTeacher.value = list[(teacherIndex.value + list.length) % list.length];
}
function changeTeacherType(type) {
  teacherType.value = type;
  teacherIndex.value = 0;
  updateTeacher();
}
function nextTeacher() {
  teacherIndex.value += 1;
  updateTeacher();
}
function handleTeacherCarouselChange(index) {
  teacherIndex.value = index;
  updateTeacher();
}
function handleTeacherPointerDown(event) {
  teacherPointerStartX = event.clientX;
}
function handleTeacherPointerUp(event) {
  if (teacherPointerStartX === null) return;
  const distance = event.clientX - teacherPointerStartX;
  teacherPointerStartX = null;
  if (Math.abs(distance) < 45 || !teacherCarousel.value) return;
  if (distance < 0) teacherCarousel.value.next();
  else teacherCarousel.value.prev();
}
function handleTeacherPointerCancel() {
  teacherPointerStartX = null;
}
function backTop() {
  window.scrollTo({ top: 0, behavior: "smooth" });
}

onMounted(() => {
  bannerTimer = setInterval(() => {
    current.value = (current.value + 1) % banners.length;
  }, 5000);
  teacherTimer = setInterval(nextTeacher, 4500);
  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("fade-in");
          observer.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.2 },
  );
  if (introImg.value) observer.observe(introImg.value);
});
onUnmounted(() => {
  clearInterval(bannerTimer);
  clearInterval(teacherTimer);
});
</script>
